import type { ComponentProps, ReactElement } from 'react';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import { Slot } from 'radix-ui';

import { cn } from '../utils/tw-merge.util';

export const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap',
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
    'rounded-xl text-sm font-medium transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-50',
  ],
  {
    variants: {
      variant: {
        default: 'bg-emerald-800 text-white hover:bg-emerald-900',
        primary:
          'bg-brand-dark-purple text-white hover:bg-brand-dark-purple/90 dark:bg-brand-purple dark:hover:bg-brand-purple/90',
        destructive: 'bg-ruby-700 text-white hover:bg-ruby-800',
        outline:
          'border border-grey-100 bg-grey-25 text-base-black shadow-sm hover:bg-grey-50 dark:border-grey-700 dark:bg-transparent dark:text-base-white dark:hover:bg-grey-800',
        secondary:
          'bg-grey-100 text-base-black hover:bg-grey-200 dark:bg-[#4c506c] dark:text-brand-light-purple dark:hover:bg-[#5b6082]',
        ghost: 'hover:bg-gray-600',
        link: 'text-emerald-800 underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 px-3',
        lg: 'h-11 px-8',
        icon: 'h-10 w-10',
        'icon-xs': 'size-7 rounded-md [&_svg]:size-4',
        'icon-sm': 'size-8 rounded-lg [&_svg]:size-4',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
        mobile: 'w-full md:w-auto',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      fullWidth: false,
    },
  },
);

type NativeButtonProps = Omit<ComponentProps<'button'>, 'style'>;

interface ButtonProps extends NativeButtonProps, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export function Button({ variant, size, fullWidth, asChild = false, className, ...props }: ButtonProps): ReactElement {
  const Comp = asChild ? Slot.Root : 'button';

  return <Comp className={cn(buttonVariants({ variant, size, fullWidth }), className)} {...props} />;
}
